import * as React from 'react';
import { Switch, Redirect } from 'react-router-dom';
import Page from './Page';
import QuizPage from './Quiz/QuizPage';
import Quiz from './Quiz/Quiz';
import ScoresPage from './Score/ScoresPage';
import Score from './Score/Score';
import LeaderboardPage from './Leaderboard/LeaderboardPage';
import AdminPage from './Admin/AdminPage';
import AdminSettings from './Admin/AdminSettings';
import CreateQuiz from './Admin/CreateQuiz';
import ViewQuiz from './Admin/ViewQuiz';
import EditQuiz from './Admin/EditQuiz';
import ProtectedRoute from './Common/ProtectedRoute';
import { isUserAdmin } from '../api/auth';
import { API } from '../api/actions';

const Empty = () => (
	<div>
	  Sorry, this page does not exist.
	</div>
);

interface Props {
	match: {
        path: string;
	};
	location: {
		hash: string;
	};
}

interface State {
	quizzes: Bee.Quiz[];
	leaderboard: any;
	loading: boolean;
	authenticating: boolean;
	authenticated: boolean;
}

class App extends React.Component<Props, State> {
	private api: API;
	constructor(props: Props) {
		super(props);
		this.state = {
			quizzes: [],
			leaderboard: [],
			loading: false,
			authenticating: true,
			authenticated: false,
		}
		this.api = new API();
	}

	componentDidMount() {
		this.onMount();
	}

	private onMount = async () => {
		const authenticated = await this.api.setup();
		this.setState({ authenticating: false, authenticated });
	}

	private getPathWithHashBrowns = (path: string) => {
		const { hash } = this.props.location;
		return `${path}${hash}`
	};

	private onQuizPublish = async (id: string) => {
		this.setState({ loading: true });
		const response = await this.api.postPublishQuiz(id);
		this.setState({ loading: false });
		return response.data
	}

	private onQuizUnPublish = async (id: string) => {
		this.setState({ loading: true });
		const response = await this.api.postUnpublishQuiz(id);
		this.setState({ loading: false });
		return response.data;
	}

	private onQuizDelete = async (id: string) => {
		this.setState({ loading: true });
		const response = await this.api.postDeleteQuiz(id);
		this.setState({ loading: false });
		return response.data;
	}

	private onQuizCreate = async (quiz: Bee.Quiz) => {
		this.setState({ loading: true });
		const response = await this.api.postCreateQuiz(quiz);
		this.setState({ loading: false });
		return response;
	}

	private onQuizSave = async (id: string, quiz: Bee.Quiz) => {
		console.log(quiz);

		this.setState({ loading: true });
		const response = await this.api.postUpdateQuiz(id, quiz);
		this.setState({ loading: false });
		return response;
	}

	private onQuizSubmit = async (quiz: Bee.Quiz) => {
		this.setState({ loading: true });
		const response = await this.api.postQuiz(quiz);
		this.setState({ loading: false });
		return response;
	}

	private getQuizzes = async (params: Bee.Params) => {
		this.setState({ loading: true });
		const response = await this.api.getQuizzes(params);
		this.setState({ loading: false });
		return response.data;
	}

	private getQuiz = async (id: string) => {
		this.setState({ loading: true });
		const response = await this.api.getQuiz(id);
		this.setState({ loading: false });
		return response.data;
	}

	private getNext = async () => {
		this.setState({ loading: true });
		const response = await this.api.getNext();
		this.setState({ loading: false });
		return response.data;
	}

	private getParticipate = async (params: Bee.Params) => {
		this.setState({ loading: true });
		const response = await this.api.getParticipate(params);
		console.log(response);
		this.setState({ loading: false });
		return response.data;
	}

	private getLeaderboard = async (params = {}) => {
		this.setState({ loading: true });
		const response = await this.api.getLeaderboard(params);
		this.setState({ loading: false });
		return response.data;
	}

  	render() {
		const admin = isUserAdmin();

		const auth = {
			authenticated: this.state.authenticated,
			authenticating: this.state.authenticating,
		};

		const adminAuth = {
			authenticated: this.state.authenticated && admin,
			authenticating: this.state.authenticating,
		};

		return (
			<Page loading={this.state.loading}>
				<Switch>
					<Redirect exact from="/" to={this.getPathWithHashBrowns("/quiz")} />
					<Redirect exact from='/example' to={this.getPathWithHashBrowns('/quiz/275bd051-ca57-4172-892c-d9d45ff803c7')} />
					<ProtectedRoute
						exact
						path="/quiz"
						{...auth}
						render={(props: any) => (
							<QuizPage
								{...props}
								getParticipate={this.getParticipate}
								getNext={this.getNext}
							/>
						)}
					/>
					<ProtectedRoute
						path="/quiz/:quizId"
						{...auth}
						render={props => (
							<Quiz
								{...props}
								onSubmit={this.onQuizSubmit}
								getQuiz={this.getQuiz}
								getParticipate={this.getParticipate}
							/>
						)}
					/>
					<ProtectedRoute
						{...auth}
						exact
						path="/score"
						render={props => <ScoresPage {...props} getQuizzes={this.getQuizzes} />} />
					<ProtectedRoute
						{...auth}
						path="/score/:quizId"
						render={props => <Score {...props} getQuiz={this.getQuiz} getParticipate={this.getParticipate} />}
					/>
					<ProtectedRoute
						{...auth}
						exact
						path="/leaderboard"
						render={() => (
							<LeaderboardPage
								getLeaderboard={this.getLeaderboard}
							/>
						)}
					/>
					<ProtectedRoute
						{...adminAuth}
						exact
						path="/admin"
						render={props => (
							<AdminPage
								{...props}
								publishQuiz={this.onQuizPublish}
								unpublishQuiz={this.onQuizUnPublish}
								deleteQuiz={this.onQuizDelete}
								getQuizzes={this.getQuizzes}
							/>
						)}
					/>
					<ProtectedRoute
						{...adminAuth}
						exact
						path="/admin/settings"
						render={props => <AdminSettings {...props} />}
					/>
					<ProtectedRoute
						{...adminAuth}
						exact
						path="/admin/view/:quizId"
						render={props => (<ViewQuiz {...props} getQuiz={this.getQuiz} />)}
					/>
					<ProtectedRoute
						{...adminAuth}
						exact
						path="/admin/create"
						render={props => (<CreateQuiz {...props} onQuizCreate={this.onQuizCreate} />)}
					/>
					<ProtectedRoute
						{...adminAuth}
						exact
						path="/admin/edit/:quizId"
						render={props => (<EditQuiz {...props} onQuizSave={this.onQuizSave} getQuiz={this.getQuiz} />)}
					/>
					<ProtectedRoute {...auth} component={Empty} />
				</Switch>
			</Page>
		);
	}
}

export default App;



// WEBPACK FOOTER //
// ./src/components/App.tsx